// src/services/hardwareBridge.js
// Electron/Capacitor-only hardware bridge. All legacy Local Hardware Service code removed.

const isElectron = !!(window?.hardwareIPC || window?.electronBridge);

// Helper to call Electron IPC safely
const ipc = async (channel, payload) => {
    try {
        if (!isElectron) throw new Error('IPC not available');
        // Prefer the modern hardwareIPC surface when channel matches
        if (window.hardwareIPC) {
            switch (channel) {
                case 'hardware:getPrinters':
                    return await window.hardwareIPC.getPrinters();
                case 'hardware:printBase64':
                    return await window.hardwareIPC.printBase64(payload);
                case 'hardware:getPcData':
                    return await window.hardwareIPC.getPcData();
                case 'hardware:getPcName':
                    return await window.hardwareIPC.getPcName();
                case 'hardware:getStoreDB':
                    return await window.hardwareIPC.getStoreDB();
                case 'hardware:setStoreDB':
                    return await window.hardwareIPC.setStoreDB(payload);
                default:
                    if (typeof window.electronBridge?.hardwareInvoke === 'function') {
                        return await window.electronBridge.hardwareInvoke(channel, payload);
                    }
            }
        }
        if (typeof window.electronBridge?.hardwareInvoke === 'function') {
            return await window.electronBridge.hardwareInvoke(channel, payload);
        }
        throw new Error('No IPC bridge available');
    } catch (e) {
        return { ok: false, error: String(e && e.message ? e.message : e) };
    }
};

export const hardwareBridge = {
    // Printers
    async getPrinters() {
        const res = await ipc('hardware:getPrinters');
        if (Array.isArray(res)) return res.map(p => p?.name || p).filter(Boolean);
        return [];
    },

    // PC Data / Name
    async getPcData() {
        const res = await ipc('hardware:getPcData');
        return res || {};
    },

    async getPcName() {
        const res = await ipc('hardware:getPcName');
        return res?.hostname || res || '';
    },

    // StoreDB
    async getStoreDB() {
        const res = await ipc('hardware:getStoreDB');
        return res || 'none';
    },

    async setStoreDB(value) {
        const res = await ipc('hardware:setStoreDB', value);
        return !!(res?.ok ?? res);
    },

    // Printing (accepts { dataUrl?, deviceName?, printer?, base64?, addFeed?, addCut? })
    async printBase64(payload) {
        payload = payload || {};
        let { dataUrl, deviceName, printer, base64, addFeed, addCut } = payload;
        if (!deviceName && printer) deviceName = printer;
        const args = base64
            ? { base64, deviceName, ...(addFeed !== undefined ? { addFeed } : {}), ...(addCut !== undefined ? { addCut } : {}) }
            : { dataUrl, deviceName };
        const res = await ipc('hardware:printBase64', args);
        if (!res?.ok) {
            const errorMsg = res?.code === 'PRINTER_MODULE_MISSING'
                ? `Print failed: Printer module not available (${res?.platform}). Try reinstalling with: sudo apt install libcups2-dev libudev-dev`
                : res?.code === 'RAW_PRINT_FAILED'
                    ? `Print failed: ${res?.message} (tried printer: ${res?.attemptedPrinter})`
                    : res?.code === 'INVALID_PRINTER'
                        ? `Print failed: Printer "${deviceName}" not found`
                        : `Print failed: ${res?.message || 'Unknown error'}`;
            console.error('[hardwareBridge.printBase64] Electron print failed:', {
                deviceName,
                hasBase64: !!base64,
                base64Length: typeof base64 === 'string' ? base64.length : 0,
                hasDataUrl: !!dataUrl,
                dataUrlPrefix: typeof dataUrl === 'string' ? dataUrl.slice(0, 32) : '',
                addFeed,
                addCut,
                response: res,
                userMessage: errorMsg
            });
            // Return object with details instead of boolean for better error handling
            return { ok: false, error: errorMsg, code: res?.code, details: res };
        }
        return { ok: true, usedPrinter: res?.usedPrinter };
    },

    // Cash drawer
    async openCashDrawer(payload) {
        const res = await ipc('hardware:openCashDrawer', payload || {});
        return !!(res?.ok);
    },

    // Updates (electron-updater via main process)
    async checkForUpdates() {
        const res = await ipc('hardware:checkForUpdates');
        return res || { available: false };
    },

    async triggerUpdate() {
        const res = await ipc('hardware:triggerUpdate');
        return !!(res?.ok);
    }
};

export default hardwareBridge;
